import React, { useMemo, useState } from 'react';
import { BarChart3, Settings2, TrendingUp, Zap } from 'lucide-react';
import { usd } from './components';
import { useT } from '../../i18n';

/**
 * Ticket generators.
 *
 * Offsets carry a unit: dollars by default, for chasing the exact price (the
 * WBTC jackpot), or a percentage of the BTC price, which matches the other
 * prize tiers since they are bands of distance in percent.
 */

const MODES = [
  { key: 'spread', label: 'bullseye.adv.autoSpread', icon: TrendingUp },
  { key: 'range', label: 'bullseye.adv.smartRange', icon: BarChart3 },
];

const STRATEGIES = [
  { key: 'gaussian', label: 'bullseye.adv.gaussian', hint: 'bullseye.adv.gaussianHint' },
  { key: 'linear', label: 'bullseye.adv.linear', hint: 'bullseye.adv.linearHint' },
  { key: 'weighted', label: 'bullseye.adv.weighted', hint: 'bullseye.adv.weightedHint' },
];

const MAX_TICKETS = 40;

/** Resolves an offset written in dollars or percent into dollars. */
const toDollars = (value, unit, base) => (unit === 'pct' ? (base * value) / 100 : value);

/** Tickets at fixed steps either side of the live price. */
export function buildSpread({ step, unit, count, direction }, base) {
  const offset = toDollars(step, unit, base);
  if (!Number.isFinite(offset) || offset <= 0 || !base) return [];

  const out = [];
  const push = (sign) => {
    for (let i = 1; i <= count; i += 1) out.push(Math.round(base + sign * offset * i));
  };

  if (direction === 'down' || direction === 'both') push(-1);
  if (direction === 'up' || direction === 'both') push(1);

  return out;
}

/** Tickets distributed between a minimum and maximum distance from the price. */
export function buildRange({ min, max, unit, count, strategy }, base) {
  const low = toDollars(min, unit, base);
  const high = toDollars(max, unit, base);
  if (!base || !Number.isFinite(low) || !Number.isFinite(high) || high < low || count < 1) {
    return [];
  }

  const out = [];
  for (let i = 0; i < count; i += 1) {
    // factor runs -1 → 1 across the ticket list; its sign picks the side.
    const factor = count > 1 ? (i / (count - 1)) * 2 - 1 : 0;
    const distance = Math.abs(factor);
    const sign = i < count / 2 ? -1 : 1;

    let magnitude;
    if (strategy === 'linear') {
      magnitude = low + (high - low) * distance;
    } else if (strategy === 'weighted') {
      magnitude = low + (high - low) * Math.pow(distance, 1.5);
    } else {
      // Gaussian: the weight is highest at the centre, so invert it to keep the
      // middle tickets close to the price and push the tails outwards.
      const x = factor * 2;
      magnitude = low + (high - low) * (1 - Math.exp((-x * x) / 2));
    }

    out.push(Math.round(base + sign * magnitude));
  }

  return out;
}

const Field = ({ label, hint, children }) => (
  <label className="block">
    <span className="lb-label mb-1.5 block">{label}</span>
    {children}
    {hint ? <span className="mt-1 block text-[0.7rem] text-white/35">{hint}</span> : null}
  </label>
);

const AdvancedBetting = ({ basePrice, betAmount, existing, onAdd }) => {
  const t = useT();
  const [mode, setMode] = useState('spread');
  const [unit, setUnit] = useState('usd');

  const [spread, setSpread] = useState({ step: 1, count: 4, direction: 'both' });
  const [range, setRange] = useState({ min: 1, max: 4, count: 8, strategy: 'gaussian' });

  const generated = useMemo(() => {
    const raw =
      mode === 'spread'
        ? buildSpread({ ...spread, unit }, basePrice)
        : buildRange({ ...range, unit }, basePrice);

    // The contract charges per ticket, so a duplicate price is money spent twice
    // on the same outcome. Drop repeats and anything already in the list.
    const seen = new Set(existing);
    const unique = [];
    raw.forEach((price) => {
      if (price > 0 && !seen.has(price)) {
        seen.add(price);
        unique.push(price);
      }
    });

    return unique.sort((a, b) => a - b).slice(0, MAX_TICKETS);
  }, [mode, unit, spread, range, basePrice, existing]);

  const cost = generated.length * (betAmount || 0);
  const unitSuffix = unit === 'pct' ? '%' : '$';

  if (!basePrice) {
    return (
      <p className="text-sm text-white/50">
        {t('bullseye.adv.waitingPrice')}
      </p>
    );
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="lb-seg">
          {MODES.map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => setMode(item.key)}
              data-active={mode === item.key}
              className="lb-seg-item"
            >
              <item.icon className="mr-2 inline h-4 w-4 align-[-2px]" aria-hidden="true" />
              {t(item.label)}
            </button>
          ))}
        </div>

        <div className="lb-seg">
          {[
            { key: 'usd', label: 'bullseye.adv.dollars' },
            { key: 'pct', label: 'bullseye.adv.percent' },
          ].map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => setUnit(item.key)}
              data-active={unit === item.key}
              data-tone="gold"
              className="lb-seg-item"
            >
              {t(item.label)}
            </button>
          ))}
        </div>
      </div>

      {unit === 'pct' ? (
        <p className="mt-3 flex items-start gap-2 rounded-xl border border-amber-400/25 bg-amber-400/[0.07] px-3 py-2.5 text-xs text-amber-200/90">
          <Settings2 className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          {t('bullseye.adv.percentWarning')}
        </p>
      ) : null}

      {/* ---------------- Settings ---------------- */}
      {mode === 'spread' ? (
        <div className="mt-5 grid gap-4 sm:grid-cols-3">
          <Field label={t('bullseye.adv.step', { unit: unitSuffix })} hint={t('bullseye.adv.stepHint')}>
            <input
              type="number"
              min={unit === 'pct' ? 0.001 : 1}
              step={unit === 'pct' ? 0.001 : 1}
              value={spread.step}
              onChange={(event) =>
                setSpread({ ...spread, step: parseFloat(event.target.value) || 0 })
              }
              className="lb-input notranslate lb-num w-full"
            />
          </Field>

          <Field label={t('bullseye.adv.ticketsPerSide')} hint={t('bullseye.adv.perSideHint')}>
            <input
              type="number"
              min="1"
              max="20"
              value={spread.count}
              onChange={(event) =>
                setSpread({ ...spread, count: Math.max(1, parseInt(event.target.value, 10) || 1) })
              }
              className="lb-input notranslate lb-num w-full"
            />
          </Field>

          <Field label={t('bullseye.adv.direction')}>
            <select
              value={spread.direction}
              onChange={(event) => setSpread({ ...spread, direction: event.target.value })}
              className="lb-input w-full"
            >
              <option value="both">{t('bullseye.adv.aboveBelow')}</option>
              <option value="up">{t('bullseye.adv.aboveOnly')}</option>
              <option value="down">{t('bullseye.adv.belowOnly')}</option>
            </select>
          </Field>
        </div>
      ) : (
        <div className="mt-5 space-y-4">
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label={t('bullseye.adv.nearest', { unit: unitSuffix })} hint={t('bullseye.adv.nearestHint')}>
              <input
                type="number"
                min="0"
                step={unit === 'pct' ? 0.001 : 1}
                value={range.min}
                onChange={(event) =>
                  setRange({ ...range, min: parseFloat(event.target.value) || 0 })
                }
                className="lb-input notranslate lb-num w-full"
              />
            </Field>

            <Field label={t('bullseye.adv.furthest', { unit: unitSuffix })} hint={t('bullseye.adv.furthestHint')}>
              <input
                type="number"
                min="0"
                step={unit === 'pct' ? 0.001 : 1}
                value={range.max}
                onChange={(event) =>
                  setRange({ ...range, max: parseFloat(event.target.value) || 0 })
                }
                className="lb-input notranslate lb-num w-full"
              />
            </Field>

            <Field label={t('bullseye.adv.totalTickets')}>
              <input
                type="number"
                min="2"
                max={MAX_TICKETS}
                value={range.count}
                onChange={(event) =>
                  setRange({ ...range, count: Math.max(2, parseInt(event.target.value, 10) || 2) })
                }
                className="lb-input notranslate lb-num w-full"
              />
            </Field>
          </div>

          <Field
            label={t('bullseye.adv.distribution')}
            hint={t(STRATEGIES.find((item) => item.key === range.strategy)?.hint || '')}
          >
            <select
              value={range.strategy}
              onChange={(event) => setRange({ ...range, strategy: event.target.value })}
              className="lb-input w-full"
            >
              {STRATEGIES.map((item) => (
                <option key={item.key} value={item.key}>
                  {t(item.label)}
                </option>
              ))}
            </select>
          </Field>
        </div>
      )}

      {/* ---------------- Preview ---------------- */}
      <div className="mt-5 rounded-2xl border border-white/10 bg-white/[0.03] p-4">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <p className="lb-label">{t('bullseye.adv.preview')}</p>
          <p className="notranslate lb-num text-xs text-white/45">
            Centred on ${usd(basePrice)}
          </p>
        </div>

        {generated.length ? (
          <>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {generated.map((price) => {
                const delta = price - basePrice;
                const tone =
                  delta === 0
                    ? 'border-amber-400/40 bg-amber-400/10 text-amber-300'
                    : delta > 0
                    ? 'border-emerald-400/25 bg-emerald-400/10 text-emerald-200'
                    : 'border-rose-400/25 bg-rose-400/10 text-rose-200';
                return (
                  <span
                    key={price}
                    className={`notranslate lb-num rounded-lg border px-2 py-1 text-xs ${tone}`}
                    title={`${delta > 0 ? '+' : ''}${usd(delta)} from the live price`}
                  >
                    ${usd(price)}
                  </span>
                );
              })}
            </div>

            <dl className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
              <div>
                <dt className="lb-label">{t('bullseye.adv.tickets')}</dt>
                <dd className="notranslate lb-num mt-1 font-bold text-white">
                  {generated.length}
                </dd>
              </div>
              <div>
                <dt className="lb-label">{t('bullseye.adv.covers')}</dt>
                <dd className="notranslate lb-num mt-1 font-bold text-white">
                  ${usd(generated[0])} to ${usd(generated[generated.length - 1])}
                </dd>
              </div>
              <div>
                <dt className="lb-label">{t('bullseye.adv.cost')}</dt>
                <dd className="notranslate lb-num mt-1 font-bold lb-gradient-text">
                  {usd(cost, 2)} USDT
                </dd>
              </div>
            </dl>
          </>
        ) : (
          <p className="mt-3 text-sm text-white/45">
            {t('bullseye.adv.nothingToAdd')}
          </p>
        )}
      </div>

      <button
        type="button"
        onClick={() => onAdd(generated)}
        disabled={!generated.length}
        className="lb-btn lb-btn-primary mt-4 w-full"
      >
        <Zap className="h-4 w-4" aria-hidden="true" />
        {generated.length === 1
          ? t('bullseye.adv.addOne')
          : t('bullseye.adv.addTickets', { count: generated.length })}
      </button>

      <p className="mt-3 flex items-start gap-2 text-xs text-white/40">
        <Settings2 className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        {t('bullseye.adv.footnote')}
      </p>
    </div>
  );
};

export default AdvancedBetting;
